<template lang="pug">
	.flex-col.space-large.full-width.creative--creative-detail(v-if="editingCreative")
		.flex-row.space-large.lr-padding.wrap.padded-children-bottom-large
			.flex-col.field.flex-basis-500
				.font-small.upper.grey {{$tCaption('Name')}}
				DxTextBox(:value.sync="editingCreative.Name", :disabled="readonly")
			template(v-if="!newCreateTemplate")
				.flex-col.field
					.font-small.upper.grey {{$tCaption('ISCI')}}
					DxTextBox(:value.sync="editingCreative.Isci", :disabled="readonly", :width="120")
				.flex-col.field
					.font-small.upper.grey {{$tCaption('Length (seconds)')}}
					DxNumberBox(:value.sync="editingCreative.Length", :disabled="readonly", :width="120")
				.flex-col.field
					.font-small.upper.grey {{$tCaption('Time zone')}}
					Dropdown(
						:getTotal="(data) => data.TotalNumberOfRecords",
						:getItems="(data) => data.Results",
						:getData="$Data.getTimeZones",
						:columns="[{ dataField: 'DisplayName', caption: $tCaption('Time zone') }]",
						displayExpr="DisplayName",
						valueExpr="Id",
						:selectedItems="editingCreative.TimeZoneId",
						:showColumnHeaders="false",
						:filtersVisible="false",
						:searchVisible="true",
						:multiple="false",
						:minSearchLength="0",
						:disabled="!!isLoading || readonly",
						@select-data="item => editingCreative.TimeZoneId = item ? item.Id : null"
					)
		.flex-row.space-large.lr-padding.wrap.padded-children-bottom
			template(v-if="!editingCreative.IsTemplate")
				.flex-col.field(v-if="!$auth.isAdvertiser()")
					.font-small.upper.grey {{$tCaption('Advertiser')}}
					Dropdown(
						:getTotal="(data) => data.TotalNumberOfRecords",
						:getItems="(data) => data.Results",
						:getData="(a,b,c) => $Data.getExternalCompanies(a,b, {...(c || {}), byType: 0, forceTokenAuth: c.byKey ? false : null })",
						:columns="[{ dataField: 'Name', caption: $tCaption('Advertiser') }]",
						displayExpr="Name",
						valueExpr="Oid",
						:selectedItems="editingCreative.Advertiser && editingCreative.Advertiser.Oid",
						:showColumnHeaders="false",
						:filtersVisible="false",
						:searchVisible="true",
						:multiple="false",
						:minSearchLength="0",
						:disabled="!!isLoading || readonly",
						@select-data="item => editingCreative.Advertiser = item"
					)
				.flex-col.field(v-if="!$auth.isAgency()")
					.font-small.upper.grey {{$tCaption('Agency')}}
					Dropdown(
						:getTotal="(data) => data.TotalNumberOfRecords",
						:getItems="(data) => data.Results",
						:getData="(a,b,c) => $Data.getExternalCompanies(a,b, {...(c || {}), byType: 2, forceTokenAuth: c.byKey ? false : null })",
						:columns="[{ dataField: 'Name', caption: $tCaption('Agency') }]",
						displayExpr="Name",
						valueExpr="Oid",
						:selectedItems="editingCreative.Agency && editingCreative.Agency.Oid",
						:showColumnHeaders="false",
						:filtersVisible="false",
						:searchVisible="true",
						:multiple="false",
						:minSearchLength="0",
						:disabled="!!isLoading || readonly",
						@select-data="item => editingCreative.Agency = item"
					)
			template(v-if="!isCreativeFile && !newCreateTemplate")
				.flex-col.space
					.flex-col
						.font-small.upper.grey {{$tCaption('Languages')}}
						.flex-col.space
							.lang(v-for="lang in supportedLanguages") {{lang.text}}
					.flex-row
						AudioTrackerButton(
							v-if="!editingCreative.IsTemplate"
							icon="fas fa-plus",
							:text="$tCaption('Add language')",
							:disabled="readonly",
							@click="$emit('add-language')",
						)
			template(v-if="!isCreativeFile && (newCreateTemplate || canSetLanguage)")
				.flex-col.field
					.font-tiny.upper.grey {{$tCaption('Main language')}}
					Dropdown(
						ref="languageDD",
						:getTotal="(data) => data.TotalNumberOfRecords",
						:getItems="(data) => data.Results",
						:getData="(pageIndex, limit, opts) => $Data.getLanguages(pageIndex, limit, opts)",
						:columns="[{ dataField: 'Name', caption: $tCaption('Name') }]",
						displayExpr="Name",
						valueExpr="Code",
						:selectedItems="editingCreative.LanguageCode",
						:showColumnHeaders="false",
						:filtersVisible="false",
						:searchVisible="true",
						:multiple="false",
						:minSearchLength="0",
						:disabled="!!isLoading || readonly",
						@select-data="item => editingCreative.LanguageCode = item.Code"
					)
			.flex-col.space(v-if="!isCreativeFile && creativeIabCategories")
				.flex-col
					.font-small.upper.grey {{$tCaption('Iab Categories')}}
					.flex-col.space(v-if="creativeIabCategoriesStr && creativeIabCategoriesStr.length > 1")
						div.iab-categories-str {{ creativeIabCategoriesStr }}
				.flex-row
					AudioTrackerButton(
						icon="fas fa-stream",
						:text="creativeIabCategories.length > 0 ? $tCaption('Update categories') : $tCaption('Add categories')",
						:disabled="readonly",
						@click="$emit('openIabCategoriesDialog')",
					)
			.flex-col(v-if="editingCreative.CreatedBy")
				.font-small.upper.grey {{$tCaption('Author')}}
				span {{createdByText}}
			.flex-col.field(v-if="editingCreative.IsTemplate")
				.font-small.upper.grey {{$tCaption('Category')}}
				Dropdown(
					:getTotal="(data) => data.TotalNumberOfRecords",
					:getItems="(data) => data.Results",
					:getData="() => $Data.getTemplateCategories()",
					:columns="[{ dataField: 'Name', caption: $tCaption('Name') }]",
					displayExpr="Name",
					valueExpr="Oid",
					:selectedItems="editingCreative.TemplateCategory && editingCreative.TemplateCategory.Oid",
					:showColumnHeaders="false",
					:filtersVisible="false",
					:searchVisible="false",
					:multiple="false",
					:minSearchLength="0",
					:disabled="readonly",
					:preloadedObjects="[editingCreative.TemplateCategory]",
					@select-data="item => editingCreative.TemplateCategory = item"
				)
		.flex-col.space-large.lr-padding(v-if="!editingCreative.IsTemplate")
			DxTabPanel(:dataSource="tabs")
				template(v-slot:title="{ data }")
					span {{data.name}}
				template(v-slot:item="{ data }")
					.root
						.tabpanel-item.padding(v-if="data.id === 'materialLinks'", :class="{ 'disabled-controls': readonly }")
							MaterialLinks(:creative="editingCreative")
						.flex-col(v-if="data.id === 'pixels'", :class="{ 'disabled-controls': readonly }")
							DxTabPanel.tab-panel-small(:dataSource="pixelTabs")
								template(v-slot:title="{ data }")
									span {{data.name}}
								template(v-slot:item="{ data }")
									.tabpanel-item.padding
										CreativeAudioPixel(v-if="data.id === 'audioPixel'", :creativeId="editingCreative.Oid")
										CreativeBanner(v-if="data.id === 'banner'", :creativeId="editingCreative.Oid")
										CreativeScriptResource(v-if="data.id === 'scriptResource'", :creativeId="editingCreative.Oid")
						.tabpanel-item.padding(v-if="data.id === 'templateFields'", :class="{ 'disabled-controls': readonly }")
							.flex-col.space-large
								CreativeTemplateFields(:editingCreative="editingCreative", :readonly="readonly")
						.tabpanel-item.padding(v-if="data.id === 'publish'")
							.flex-col.space-large
								.flex-row.space-medium.wrap(:class="{ 'disabled-controls': readonly }")
									.flex-col
										.font-small.upper.grey {{$tCaption('Sub-insertions grouping')}}
										DxSelectBox(:data-source="groupingTypesOnPublishing", valueExpr="id", displayExpr="name", :value.sync="editingCreative.GroupingTypeOnPublishing")
								CreativePublishSchedule(:creative="editingCreative", @create-template-field="onCreateTemplateField", :readonly="readonly")
						.tabpanel-item.padding(v-if="data.id === 'excelData'")
							CreativeExcelData.full-width(:creative="creative", :allowManaging="true", :allowUpload="true", :tracker="tracker")
								.font-big.upper.grey(slot="header-left") {{$tCaption('Excel data')}}
		.flex-col.space-large.lr-padding(v-if="editingCreative.IsTemplate")
			DxTabPanel(:dataSource="templateTabs")
				template(v-slot:title="{ data }")
					span {{data.name}}
				template(v-slot:item="{ data }")
					.tabpanel-item.padding
						.tabpanel-item.padding(v-if="data.id === 'publish'")
							.flex-col.space-large
								.flex-row.space-medium.wrap(:class="{ 'disabled-controls': readonly }")
									.flex-col
										.font-small.upper.grey {{$tCaption('Sub-insertions grouping')}}
										DxSelectBox(:data-source="groupingTypesOnPublishing", valueExpr="id", displayExpr="name", :value.sync="editingCreative.GroupingTypeOnPublishing")
								CreativePublishSchedule(:creative="editingCreative", @create-template-field="onCreateTemplateField", :readonly="readonly")
						template(v-if="data.id === 'template'")
							.font-big.upper.grey {{$tCaption('Template info')}}
							.flex-row.space-medium
								.flex-col.space-large.align-center(v-if="!newCreateTemplate")
									.image-holder
										.image(v-if="imageUrl", :style="{ background: `url(${imageUrl})` }")
									FileUploader(:text="$tCaption('Upload image')", icon="fas fa-cloud-upload-alt", :disabled="readonly", @file-selected="onImageSelected", @upload="onImageChange", :popupConfig="{ height: 520, width: 520 }")
										.flex-row.align-center(v-if="imageData", slot="bottom-content")
											VueCropper.user-profile.cropper-element(ref="cropper",
												:aspectRatio="1",
												:src="imageData")
								.flex-col.space-large.flex-1
									.flex-row
										CreativeTemplateFields(:editingCreative="editingCreative", :readonly="readonly")
									.flex-col.space-medium
										.flex-col.flex-1
											.font-small.upper.grey {{$tCaption('Description')}}
											DxTextArea(:value.sync="editingCreative.Description", :height="80", :disabled="readonly", stylingMode="filled")
										.flex-row.space-medium
											DxCheckBox(v-if="!editingCreative.IsNewsMLTemplate", :text="$tCaption('Match Template insertion\\\'s Length')", :value="editingCreative.JsonConfig.adjustLengthToTemplate == null ? true : editingCreative.JsonConfig.adjustLengthToTemplate", 
												@value-changed="e => $set(editingCreative.JsonConfig, 'adjustLengthToTemplate', e.value)", :disabled="readonly")
											DxCheckBox(v-if="$auth.isAdmin()", :text="$tCaption('Is product')", :value.sync="editingCreative.IsProduct", :disabled="readonly")
									template(v-if="editingCreative.IsProduct")
										.flex-row.flex-1.align-end.space
											.flex-col.field
												.font-tiny.upper.grey {{$tCaption('Price')}}
												DxNumberBox(:value.sync="editingCreative.Price", :disabled="readonly")
											.flex-col.field
												.font-tiny.upper.grey {{$tCaption('Currency')}}
												Dropdown(
													:getTotal="(data) => data.TotalNumberOfRecords",
													:getItems="(data) => data.Results",
													:getData="() => $Data.getCurrencies()",
													:columns="[{ dataField: 'Name', caption: $tCaption('Name') }]",
													displayExpr="Code",
													valueExpr="Code",
													:selectedItems="editingCreative.CurrencyCode",
													:showColumnHeaders="false",
													:filtersVisible="false",
													:searchVisible="false",
													:multiple="false",
													:minSearchLength="0",
													:disabled="!!isLoading || readonly",
													@select-data="item => editingCreative.CurrencyCode = item.Code"
												)
													dx-validator
														dx-required-rule(:message="$tCaption('Currency is required')")
						template(v-if="data.id === 'newsml'")
							CreativeDetailNewsML(ref="detailNewsML", :editingCreative="editingCreative", :readonly="readonly")
		Popup(v-if="isServicesManagerVisible", :popupVisible="isServicesManagerVisible", :width="760", :height="640", :title="$tCaption('Services manager')")
			CreativeServicesManager(ref="servicesManager", :creative="editingCreative", :insertions="insertions", @edit-insertion="ins => $emit('edit-insertion', ins)",
				:templateFields="editingCreative.JsonConfig.templateFields", @changed-insertions="v => changedInsertions = v || {}", @create-template-field="onCreateTemplateField", :changedInsertionsData="changedInsertions")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close')", @click="isServicesManagerVisible = false")
</template>

<script>
	import DxButton from 'devextreme-vue/button';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxTextArea from 'devextreme-vue/text-area';
	import DxNumberBox from 'devextreme-vue/number-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import FileUploader from 'app-core/FileUploader.vue';
	import DxCheckBox from 'devextreme-vue/check-box';
	import AudioTrackerButton from 'view/audioTracker/AudioTrackerButton.vue';
	import Dropdown from 'app-core/Dropdown.vue';
	import VueCropper from 'vue-cropperjs';
	import 'cropperjs/dist/cropper.css';
	import DxValidationGroup from 'devextreme-vue/validation-group';
	import DxValidator, { DxRequiredRule, DxCustomRule } from 'devextreme-vue/validator';
	import DxTabPanel from 'devextreme-vue/tab-panel';
	import CreativeAudioPixel from './CreativeAudioPixel.vue';
	import CreativeBanner from './CreativeBanner.vue';
	import CreativeScriptResource from './CreativeScriptResource.vue';
	import MaterialLinks from './MaterialLinks.vue';
	import CreativeServicesManager from './CreativeServicesManager.vue';
	import Popup from 'app-core/Popup.vue';
	import creativeServicesHelper from './creative-services-helper';
	import BaseParameterSelector from 'view/webServiceCaller/BaseParameterSelector.vue';
	import CreativePublishSchedule from './CreativePublishSchedule.vue';
	import CreativeTemplateFields from './CreativeTemplateFields.vue';
	import CreativeExcelData from './CreativeExcelData.vue';
	import CreativeDetailNewsML from './CreativeDetailNewsML.vue';

	export default {
		translationCategory: 'creative.Detail',
		name: 'creative-detail',
		components: {
			DxButton, DxTextBox, DxTextArea, DxNumberBox, FileUploader,	AudioTrackerButton, Dropdown, VueCropper, DxValidationGroup, DxValidator,
			DxRequiredRule, DxCustomRule, DxTabPanel, CreativeAudioPixel, CreativeBanner, CreativeScriptResource, DxSelectBox, MaterialLinks,
			DxCheckBox, CreativeServicesManager, Popup, BaseParameterSelector, CreativePublishSchedule, CreativeTemplateFields, CreativeExcelData, CreativeDetailNewsML
		},
		props: [
			'editingCreative', 'readonly', 'isLoading', 'creativeIabCategoriesStr', 'creativeIabCategories', 'languages', 'currencies',
			'newCreateTemplate', 'canSetLanguage', 'isCreativeFile', 'insertions', 'creative', 'tracker'
		],

		data() {
			this.$templateFieldDataTypes = [...this.$Data.externalServicesParametersDataTypes(), 'service', ...Object.keys(this.$Data.complexExternalServicesTypes)]
				.map(f => ({ id: f, name: this.$Data.complexExternalServicesTypes[f]?.name || this.$Data.capitalize(f) }));
			const pixelTabs = [
				{ id: 'audioPixel', name: this.$tCaption('Audio pixels') },
				{ id: 'banner', name: this.$tCaption('Banners') },
				{ id: 'scriptResource', name: this.$tCaption('Script resources') }
			]

			const tabs = [
				{ id: 'excelData', name: this.$tCaption('Data') },
				{ id: 'publish', name: this.$tCaption('Publishing') },
				{ id: 'pixels', name: this.$tCaption('Ad Serving') },
			]

			const templateTabs = [
				{ id: 'template', name: this.$tCaption('Template') },
				{ id: 'newsml', name: this.$tCaption('NewsML') },
				{ id: 'publish', name: this.$tCaption('Publishing') },
			]

			if (this.$auth.isStationOwner()) {
				tabs.splice(2, 0, { id: 'materialLinks', name: this.$tCaption('Material links') });
			}

			if (!this.editingCreative.IsTemplate) {
				tabs.splice(2,0, { id: 'templateFields', name: this.$tCaption('Template fields') });
			}
			
			return {
				imageData: null,
				fileName: null,
				changedIdsTemplateFields: [],
				tabs,
				pixelTabs,
				templateTabs,
				isServicesManagerVisible: false,
				changedInsertions: {},
				groupingTypesOnPublishing: [
					{ id: 0, name: this.$tCaption('By target') },
					{ id: 1, name: this.$tCaption('By name') },
					{ id: 2, name: this.$tCaption('Combinations') }
				]
			};
		},
		computed: {
			supportedLanguages() {
				let langs = `${this.editingCreative.AllSupportedLanguages || 'en-US'}`.split(',');
				return (this.languages || []).filter(f => langs.some(v => `${v}`.trim() === f.locale));
			},
			imageUrl() {
				return this.imageData || this.editingCreative?.ImageUrl;
			},
			createdByText() {
				return `${this.editingCreative.CreatedBy?.Contact?.Title || this.editingCreative.CreatedBy?.UserName}, ${this.$moment(this.editingCreative.Created).format('LLL')}`;
			}
		},
		watch: {
			'editingCreative.IsNewsMLTemplate'(v) {
				if (v) {
					this.$set(this.editingCreative.JsonConfig, 'adjustLengthToTemplate', false);
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.creativeDetailObj = this;
		},
		destroyed() {
			delete this.$templateFieldDataTypes;
		},
		methods: {
			onCreateTemplateField(templateField, callback) {
				templateField.id = this.$Data.getNextNaming(templateField.id, (this.editingCreative.JsonConfig.templateFields || []).map(f => f.id));
				templateField.description = this.$Data.getNextNaming(templateField.description, (this.editingCreative.JsonConfig.templateFields || []).map(f => f.description));
				this.editingCreative.JsonConfig.templateFields.push(templateField);
				this.$notifySuccess(this.$tCaption('Template field has been successfully generated'));
				if (callback)
					callback(templateField);
			},
			save() {
				if (__DEBUG__)
					console.debug('Creative detail save', { changedInsertions: this.changedInsertions });
				if (this.$refs.detailNewsML) this.$refs.detailNewsML.save();
				if (!this.changedInsertions) return;
				Object.keys(this.changedInsertions).map(id => {
					const item = this.changedInsertions[id];
					if (item.ssml) {
						item.insertion.Ssml = item.ssml;
						this.$emit('regenerate-insertion', id);
					}
				});
			},
			openServicesManager() {
				this.isServicesManagerVisible = false;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.isServicesManagerVisible = true;
				});
			},
			getTemplateFieldDataTypes() {
				return Promise.resolve(this.$templateFieldDataTypes);
			},
			onImageSelected(v) {
				if (__DEBUG__)
					console.log('On image selected', v);
				this.imageData = null;
				var fr = new FileReader;
				fr.readAsDataURL(v[0]);
				fr.onloadend = () => {
					this.imageData = fr.result;
					this.fileName = v[0]?.name;
				};
			},
			onImageChange() {
				if (__DEBUG__)
					console.debug('On image change');
				this.imageData = this.$refs.cropper.getCroppedCanvas({ width: 256, height: 256 }).toDataURL(data => console.log(data));
				this.editingCreative.ImageUrl = this.imageData;
				var img = `${this.imageData}`.split('base64,')[1];
				this.$Data.postExternalApi('Service/SetObjectImage', {
					ObjectId: this.editingCreative.Oid,
					ObjectType: 10,
					Base64Content: img,
					FieldName: 'Image',
					FileName: this.fileName,
					FileExtension: `${this.fileName}`.split('.').pop()
				}).then(r => {
					if (__DEBUG__)
						console.debug('Image uploaded', { r });
					if (r.Success) {
						this.$notifySuccess(this.$tCaption('Image uploaded'));
					}
				});
			},
			regenerateInsertionsByChangedTemplateFields(oldTemplateFields, newTemplateFields) {
				const changedTemplateFields = [];
				(newTemplateFields || []).filter(n => {
					const ot = (oldTemplateFields || []).find(o => o.id === n.id && o.default !== n.default);
					if (ot) {
						changedTemplateFields.push(n);
					}
				});

				const insertions = creativeServicesHelper.filterInsertionsByTemplateField(this.insertions || [], changedTemplateFields.map(f => f.id));

				if (__DEBUG__)
					console.debug('regenerateInsertionsByChangedTemplateFields', { changedTemplateFields, oldTemplateFields, newTemplateFields, insertions });

				if (insertions.length)
					this.$emit('regenerate-insertion', insertions.map(f => f.Oid));
			}
		},
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.creative--creative-detail {
		.image-holder {
			height: 280px;
			width: 280px;
			border-radius: 8px;
			background: rgba(0, 0, 0, 0.03);
			overflow: hidden;

			border: 1px solid rgba(0, 0, 0, 0.01);
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

			.image {
				height: 100%;
				width: 100%;
				background-size: contain !important;
			}
		}

		&.cropper-element {
			height: 280px;
			width: 100%;
		}

		.tabpanel-item {
			background: rgba(255,255,255,0.2);
		}
	}
</style>
